"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    investees: Record<string, string>;
    assets: Record<string, string>;
    investors: { name: string; units: string; yen: string }[];
    zoneInvestees: string;
    zoneFund: string;
    zoneInvestors: string;
    inflowHeading: string;
    inflowSub: string;
    netAssets: string;
    cutLabel: string;
    investorLine: (name: string, units: string) => string;
    title: string;
    description: string;
  }
> = {
  ja: {
    investees: { equityA: "株式A", equityB: "株式B", govBond: "国債", corpBond: "社債" },
    assets: { equity: "株式", bond: "債券", cash: "現金" },
    investors: [
      { name: "投資家A", units: "100口", yen: "5万円" },
      { name: "投資家B", units: "300口", yen: "15万円" },
      { name: "投資家C", units: "50口", yen: "2.5万円" },
    ],
    zoneInvestees: "投資先",
    zoneFund: "ファンド",
    zoneInvestors: "投資家",
    inflowHeading: "配当・利子・値上がり益",
    inflowSub: "投資先からの収益でファンドの純資産が積み上がる",
    netAssets: "ファンドの純資産（110億円）",
    cutLabel: "決算で一部を切り取って分配 → 5億円（1口あたり 500円）",
    investorLine: (name, units) => `${name}（${units}）`,
    title: "純資産の一部を切り取って、投資家へ分配する",
    description:
      "投資先の株式から配当と値上がり益、債券から利子と値上がり益を受け取ってファンドの純資産が積み上がり、その一部を決算で投資家へ分配します（数値は概念的な概算）。",
  },
  en: {
    investees: { equityA: "Equity A", equityB: "Equity B", govBond: "Gov. bonds", corpBond: "Corp. bonds" },
    assets: { equity: "Equities", bond: "Bonds", cash: "Cash" },
    investors: [
      { name: "Investor A", units: "100 units", yen: "¥50,000" },
      { name: "Investor B", units: "300 units", yen: "¥150,000" },
      { name: "Investor C", units: "50 units", yen: "¥25,000" },
    ],
    zoneInvestees: "What it invests in",
    zoneFund: "Fund",
    zoneInvestors: "Investors",
    inflowHeading: "Dividends, interest, and price gains",
    inflowSub: "Income from the holdings builds up the fund's net assets",
    netAssets: "Fund net assets (¥11.0bn)",
    cutLabel: "At the accounting date, part is carved out and distributed → ¥0.5bn (¥500 per unit)",
    investorLine: (name, units) => `${name} (${units})`,
    title: "Part of the net assets is carved out and distributed to investors",
    description:
      "Dividends and price gains from equities, plus interest and price gains from bonds, build up the fund's net assets, and part of that is distributed to investors at the accounting date (figures are conceptual approximations).",
  },
};

// ── Colors (3-1 に揃える: 株式=sky, 債券=amber, 現金=gray) ────────

const C = {
  equity: COLOR.sky,
  bond: COLOR.amber,
  cash: "#64748b",
  person: "#64748b",
  zone: "currentColor",
};

// ── Data ──────────────────────────────────────────────────────────

type Investee = { key: string; type: "equity" | "bond"; cx: number };

const INVESTEES: Investee[] = [
  { key: "equityA", type: "equity", cx: 95 },
  { key: "equityB", type: "equity", cx: 175 },
  { key: "govBond", type: "bond", cx: 285 },
  { key: "corpBond", type: "bond", cx: 365 },
];

const SEGMENTS = [
  { key: "equity", pct: 55, color: C.equity },
  { key: "bond", pct: 30, color: C.bond },
  { key: "cash", pct: 15, color: C.cash },
];

const INVESTORS = [
  { id: "a" as const },
  { id: "b" as const },
  { id: "c" as const },
];

// ── Layout ────────────────────────────────────────────────────────

const VW = 460;

// Zone geometry
const zonePadX = 14;

// Zone 1: 投資先
const z1Y = 30;
const z1H = 68;
const investeeCy = z1Y + 34;
const investeeR = 20;

// Inflow labels (between zone 1 and zone 2)
const inflowY = z1Y + z1H + 18;

// Zone 2: ファンド
const z2Y = inflowY + 18;
const z2H = 88;
const barX = zonePadX + 18;
const barW = VW - (zonePadX + 18) * 2;
const barY = z2Y + 34;
const barH = 40;

// Cut-off label (between zone 2 and zone 3)
const cutY = z2Y + z2H + 18;

// Zone 3: 投資家
const z3Y = cutY + 18;
const z3H = 96;
const invRowY = z3Y + 34;
const invXs = [95, 230, 365];

const VH = z3Y + z3H + 18;

// ── Zone background ─────────────────────────────────────────────

function Zone({
  y,
  h,
  title,
}: {
  y: number;
  h: number;
  title: string;
}) {
  return (
    <g>
      <rect
        x={zonePadX}
        y={y}
        width={VW - zonePadX * 2}
        height={h}
        rx={6}
        fill={C.zone}
        fillOpacity={0.025}
        stroke={C.zone}
        strokeWidth="1"
        strokeOpacity={0.15}
      />
      <text
        x={zonePadX + 10}
        y={y - 6}
        fontSize="9"
        fill="currentColor"
        fillOpacity={0.55}
        fontWeight="700"
      >
        {title}
      </text>
    </g>
  );
}

// ── Inflow / cut-off arrow between zones ───────────────────────

function ZoneArrow({ y, label }: { y: number; label: string }) {
  return (
    <g>
      <text
        x={VW / 2}
        y={y - 2}
        textAnchor="middle"
        fontSize="9.5"
        fill="currentColor"
        fontWeight="600"
      >
        {label}
      </text>
      <path
        d={`M ${VW / 2} ${y + 4} L ${VW / 2} ${y + 14}`}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeOpacity={0.45}
      />
      <path
        d={`M ${VW / 2 - 5} ${y + 10} L ${VW / 2} ${y + 16} L ${VW / 2 + 5} ${y + 10}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeOpacity={0.45}
      />
    </g>
  );
}

// ── Illustration ──────────────────────────────────────────────────

function PayoutIllustration() {
  const t = STRINGS[useDocLocale()];
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* ── Zone 1: 投資先 ─────────────────────────────────────── */}
      <Zone y={z1Y} h={z1H} title={t.zoneInvestees} />
      {INVESTEES.map((inv) => {
        const color = inv.type === "equity" ? C.equity : C.bond;
        return (
          <g key={inv.key}>
            <circle
              cx={inv.cx}
              cy={investeeCy}
              r={investeeR}
              fill={color}
              fillOpacity={0.14}
              stroke={color}
              strokeWidth="1.2"
            />
            <text
              x={inv.cx}
              y={investeeCy + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="9"
              fill={color}
              fontWeight="600"
            >
              {t.investees[inv.key]}
            </text>
          </g>
        );
      })}

      {/* ── Inflow between Zone 1 and Zone 2 ──────────────────── */}
      <g>
        <text
          x={VW / 2}
          y={inflowY - 2}
          textAnchor="middle"
          fontSize="9.5"
          fill="currentColor"
          fontWeight="600"
        >
          {t.inflowHeading}
        </text>
        <text
          x={VW / 2}
          y={inflowY + 10}
          textAnchor="middle"
          fontSize="7.5"
          fill="currentColor"
          fillOpacity={0.55}
        >
          {t.inflowSub}
        </text>
        {INVESTEES.map((inv) => (
          <g key={`arr-${inv.key}`}>
            <path
              d={`M ${inv.cx} ${investeeCy + investeeR + 4} L ${inv.cx} ${barY - 8}`}
              stroke="currentColor"
              strokeWidth="1"
              strokeOpacity={0.28}
              strokeDasharray="3 3"
            />
            <path
              d={`M ${inv.cx - 4} ${barY - 13} L ${inv.cx} ${barY - 7} L ${inv.cx + 4} ${barY - 13}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeOpacity={0.4}
            />
          </g>
        ))}
      </g>

      {/* ── Zone 2: ファンド ──────────────────────────────────── */}
      <Zone y={z2Y} h={z2H} title={t.zoneFund} />
      <text
        x={VW / 2}
        y={barY - 10}
        textAnchor="middle"
        fontSize="10.5"
        fill="currentColor"
        fontWeight="700"
      >
        {t.netAssets}
      </text>
      {(() => {
        let offset = 0;
        return SEGMENTS.map((seg) => {
          const w = (seg.pct / 100) * barW;
          const sx = barX + offset;
          offset += w;
          return (
            <g key={seg.key}>
              <rect
                x={sx}
                y={barY}
                width={w}
                height={barH}
                rx={3}
                fill={seg.color}
                fillOpacity={0.22}
                stroke={seg.color}
                strokeWidth="1"
                strokeOpacity={0.55}
              />
              <text
                x={sx + w / 2}
                y={barY + barH / 2 - 3}
                textAnchor="middle"
                fontSize="10"
                fill={seg.color}
                fontWeight="600"
              >
                {t.assets[seg.key]}
              </text>
              <text
                x={sx + w / 2}
                y={barY + barH / 2 + 11}
                textAnchor="middle"
                fontSize="11"
                fill={seg.color}
                fontWeight="700"
              >
                {seg.pct}%
              </text>
            </g>
          );
        });
      })()}

      {/* ── Cut-off between Zone 2 and Zone 3 ─────────────────── */}
      <ZoneArrow y={cutY} label={t.cutLabel} />

      {/* ── Zone 3: 投資家 ────────────────────────────────────── */}
      <Zone y={z3Y} h={z3H} title={t.zoneInvestors} />
      {INVESTORS.map((inv, i) => (
        <g key={inv.id}>
          {/* Person icon */}
          <circle cx={invXs[i]} cy={invRowY - 6} r={7} fill={C.person} />
          <rect x={invXs[i] - 8} y={invRowY + 3} width={16} height={9} rx={4} fill={C.person} />
          <text
            x={invXs[i]}
            y={invRowY + 28}
            textAnchor="middle"
            fontSize="9"
            fill={C.person}
            fontWeight="600"
          >
            {t.investorLine(t.investors[i]!.name, t.investors[i]!.units)}
          </text>
          <text
            x={invXs[i]}
            y={invRowY + 43}
            textAnchor="middle"
            fontSize="11"
            fill={C.equity}
            fontWeight="700"
          >
            {t.investors[i]!.yen}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ── Export ─────────────────────────────────────────────────────────

export function DocFundDistributionPayoutDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <PayoutIllustration />
    </IllustrationCard>
  );
}
